import type {
  NeonHttpQueryResult,
  drizzle as drizzleNeon,
} from 'drizzle-orm/neon-http'
import type { drizzle as drizzlePostgres } from 'drizzle-orm/postgres-js'
import { and, eq, inArray, like, sql, or, SQL } from 'drizzle-orm'
import { entry } from './schema'
import { SpaceLineage } from './get-repo-lineage'
import { invariant } from '@epic-web/invariant'
import { Block, BlockRelationship } from '../replicache'
import type { PgTransaction } from 'drizzle-orm/pg-core'

// Override the DB types to include our custom execute method
export type DB = Omit<ReturnType<typeof drizzlePostgres>, 'execute'> & {
  execute<T = Record<string, unknown>>(query: SQL): Promise<T[]>
}

export type DBNeonHttp = Omit<ReturnType<typeof drizzleNeon>, 'execute'> & {
  execute<T = Record<string, unknown>>(query: SQL): Promise<{ rows: T[] }>
}

export type Executor = Omit<PgTransaction<any, any, any>, 'execute'> & {
  execute<T = Record<string, unknown>>(query: SQL): Promise<T[]>
}

export type PostgresExecuteResult = Record<string, unknown>[]
export type NeonHttpExecuteResult = NeonHttpQueryResult<Record<string, unknown>>

export function isDBNeonHttpResult<T extends Record<string, unknown>>(
  result: T[] | { rows: T[] }
): result is { rows: T[] } {
  return 'rows' in result
}

export type JSONValue =
  | null
  | string
  | boolean
  | number
  | Array<JSONValue>
  | JSONObject

export type JSONObject = { [key: string]: JSONValue | undefined }

export type Entry<Value extends JSONValue> = {
  key: string
  value: Value
  lastmodified: Date
  version: number
  spaceid: string
}

type EntryType =
  | 'block'
  | 'commit'
  | 'branch'
  | 'repository'
  | 'team'
  | 'sdk-version'
  | 'user-preferences'
  | 'event'
  | 'client'
  | 'integration'
  | 'content-request'
  | 'suggested-branch'
  | 'comment'
  | 'workflow-run'
  | 'br'

type ValueFilter =
  | { path: string; equals: string | boolean }
  | { path: string; in: string[] }

export async function findEntry<Value extends JSONValue>({
  spaceID,
  repoId,
  forkedRepoId,
  teamId,
  entryType,
  valueFilter,
  id = '',
  idStartsWith,
  db,
  includeDeleted = false,
  log = false,
}: {
  spaceID: string | string[] | false
  repoId?: string
  forkedRepoId?: string
  teamId?: string
  entryType: EntryType
  valueFilter?: ValueFilter
  id?: string
  idStartsWith?: string
  db: DB | DBNeonHttp | Executor
  includeDeleted?: boolean
  log?: boolean
}) {
  let repoQuery
  if (repoId) {
    repoQuery = like(entry.spaceid, `%/repo/${repoId}`)
    if (forkedRepoId) {
      repoQuery = or(repoQuery, like(entry.spaceid, `%/repo/${forkedRepoId}`))
    }
  } else if (spaceID) {
    if (Array.isArray(spaceID)) {
      repoQuery = inArray(entry.spaceid, spaceID)
    } else {
      repoQuery = eq(entry.spaceid, spaceID)
    }
  }

  const q = db
    .select()
    .from(entry)
    .where(
      and(
        like(
          entry.key,
          `${entryType}/${idStartsWith ? `${idStartsWith}%` : '%'}`
        ),
        id ? eq(entry.key, `${entryType}/${id}`) : undefined,
        includeDeleted
          ? undefined
          : sql`(value->>'deletedAt' IS NULL OR value->>'deletedAt' = 'null')`,
        valueFilter
          ? 'equals' in valueFilter
            ? sql`(value->>${valueFilter.path})${sql.raw(
                typeof valueFilter.equals === 'boolean' ? `::boolean` : ''
              )} = ${valueFilter.equals}`
            : sql`value->>${valueFilter.path} IN ${valueFilter.in}`
          : undefined,
        repoQuery,
        !repoQuery && teamId
          ? like(entry.spaceid, `team/${teamId}%`)
          : undefined
      )
    )
    .limit(1)

  if (log) {
    // eslint-disable-next-line no-console
    console.log(q.toSQL())
  }

  const [queryResult] = await q

  if (!queryResult) return null

  const typedResult = queryResult as Omit<typeof queryResult, 'value'> & {
    value: Value
  }

  return typedResult
}

export const getFindEntriesQuery = ({
  spaceID,
  repoId,
  forkedRepoId,
  teamId,
  spaceIDStartsWith,
  entryType,
  valueFilter,
  ids,
  db,
  includeDeleted = false,
}: {
  spaceID: string | string[] | false
  repoId?: string
  forkedRepoId?: string
  teamId?: string
  spaceIDStartsWith?: string
  entryType: EntryType
  valueFilter?: ValueFilter
  ids?: string[]
  db: DB | DBNeonHttp
  includeDeleted?: boolean
}) => {
  let repoQuery
  if (repoId) {
    repoQuery = like(entry.spaceid, `%/repo/${repoId}`)
    if (forkedRepoId) {
      repoQuery = or(repoQuery, like(entry.spaceid, `%/repo/${forkedRepoId}`))
    }
  } else if (spaceID) {
    if (Array.isArray(spaceID)) {
      repoQuery = inArray(entry.spaceid, spaceID)
    } else {
      repoQuery = eq(entry.spaceid, spaceID)
    }
  }

  return db
    .select()
    .from(entry)
    .where(
      and(
        like(entry.key, `${entryType}/%`),
        ids
          ? inArray(
              entry.key,
              ids.length > 0 ? ids.map((id) => `${entryType}/${id}`) : ['noop']
            )
          : undefined,
        includeDeleted
          ? undefined
          : sql`(value->>'deletedAt' IS NULL OR value->>'deletedAt' = 'null')`,
        valueFilter
          ? 'equals' in valueFilter
            ? sql`(value->>${valueFilter.path})${sql.raw(
                typeof valueFilter.equals === 'boolean' ? `::boolean` : ''
              )} = ${valueFilter.equals}`
            : sql`value->>${valueFilter.path} IN ${valueFilter.in}`
          : undefined,
        repoQuery,
        teamId ? like(entry.spaceid, `team/${teamId}%`) : undefined,
        spaceIDStartsWith
          ? like(entry.spaceid, `${spaceIDStartsWith}%`)
          : undefined
      )
    )
}

type GetNestedBlocksSQL = {
  spaceLineage: SpaceLineage
  rootBlockId: string
  cvrKey: string | null | undefined
  cvrSyncSequence: number | null | undefined
  additionalFilters?: (entryKey: string) => SQL<unknown>
  additionalBRFilters?: (entryKey: string) => SQL<unknown>
  mode?: 'brOnly' | 'blockOnly' | 'both'
  paginate?: {
    limit: number
    cursor: string
  }
  structuralOnly?: boolean
  searchThroughMilestones?: string[] | null
}

/**
 * Gets nested blocks using the temporal branch lineage model:
 * - Root block contains branchId and branchLineage
 * - BRs have branchId, validFrom, validTo, and isCommitted flags
 * - Each BR belongs to a specific branch with temporal validity
 */
export const getNestedBlocksSQL = ({
  spaceLineage,
  rootBlockId,
  cvrKey,
  cvrSyncSequence,
  additionalFilters,
  additionalBRFilters,
  mode = 'both',
  paginate,
  structuralOnly,
  searchThroughMilestones,
}: GetNestedBlocksSQL): SQL<unknown> => {
  const spaces = spaceLineage.map((space) => sql`${space}`)
  const mainSpaceId = spaceLineage[0]
  invariant(mainSpaceId, `main space id should always be provided.`)
  const milestonesJoined = searchThroughMilestones?.length
    ? sql.join(
        searchThroughMilestones.map((m) => sql`${m}`),
        sql`,`
      )
    : null

  let result = sql`
WITH space_lineage AS (
  -- Create a table with spaces and their priorities based on position in lineage
  SELECT 
    space_id,
    ordinality AS priority
  FROM unnest(ARRAY[${sql.join(
    spaces,
    sql`,`
  )}]) WITH ORDINALITY AS t(space_id, ordinality)
),
root_data AS (
  -- Get root block with its branch info and timestamps
  SELECT DISTINCT ON (key)
    key as root_key,
    value->>'id' as root_id,
    value->>'branchId' as branch_id,
    value->'branchLineage' as branch_lineage,
    value->>'createdAt' as created_at,
    value->>'isCommitted' as is_committed,
    value->>'isInlineSuggestion' as is_inline_suggestion
  FROM entry
  JOIN space_lineage sl ON sl.space_id = entry.spaceid
  WHERE key = ${`block/${rootBlockId}`} 
    AND spaceid IN (${sql.join(spaces, sql`,`)})
  ORDER BY key, sl.priority
  LIMIT 1
),
-- Expand the branch lineage to include current branch and all ancestors
branch_lineage AS (
  SELECT 
    t.branch_id,
    t.last_synced_at,
    t.ordinality AS priority
  FROM root_data,
  LATERAL unnest(
    ARRAY[root_data.branch_id] || 
    CASE 
      WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
      ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'branchId')
    END,
    ARRAY[NULL] || 
    CASE 
      WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
      ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'lastSyncedAt')
    END
  ) WITH ORDINALITY AS t(branch_id, last_synced_at, ordinality)
),
-- Get all relevant block relationships
possible_brs AS (
  SELECT DISTINCT ON (br.value->>'sourceBlockId')
    br.key,
    br.value, 
    br.version, 
    br.spaceid,
    br.lastmodified,
    bl.priority as branch_priority
  FROM branch_lineage bl
  JOIN entry br ON (
    -- Match the branch ID
    br.value->>'branchId' = bl.branch_id

    -- Committed check
    AND (
      (SELECT is_committed FROM root_data) = 'false' OR
      br.value->>'isCommitted' = 'true'
    )

    -- Not discarded
    AND br.value->>'discardedAt' IS NULL

    ${
      milestonesJoined
        ? sql`
    -- Only get BRs that have a milestone referencing their blockId
    AND (br.value->>'sourceBlockId' IN (${milestonesJoined})${structuralOnly ? sql` OR br.value->>'structural' = 'true'` : sql``})`
        : sql``
    }

    ${additionalBRFilters ? sql`AND (${additionalBRFilters('br')})` : sql``}

    -- Additional branch-specific checks
    AND (
      -- If it's current branch, no additional check needed
      (bl.priority = 1)

      -- For ancestor branches
      OR 
      (
        bl.priority > 1 
        -- Must be committed for ancestor branches (or inline suggestion but not leaked from other inline suggestion-branches)
        AND (br.value->>'isCommitted' = 'true' OR ((SELECT is_inline_suggestion FROM root_data) = 'true' and br.value->>'isInlineSuggestion' is null))
        -- BR was valid at checkout time
        AND (br.value->>'validFrom')::timestamptz <= bl.last_synced_at::timestamptz
        AND (br.value->>'validTo' IS NULL OR (br.value->>'validTo')::timestamptz >= bl.last_synced_at::timestamptz)
      )
    )
  )
  JOIN space_lineage sl ON sl.space_id = br.spaceid
  WHERE br.spaceid IN (${sql.join(spaces, sql`,`)})
    AND br.key like 'br/%'

    -- ALWAYS check if BR is valid at root creation time (root temporal check)
    AND (br.value->>'validFrom')::timestamptz <= (SELECT created_at FROM root_data)::timestamptz
    -- Don't perform validTo check YET, as we want to make the distinct on preserve overrides
    -- AND (br.value->>'validTo' IS NULL OR (br.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data)::timestamptz)
    ${structuralOnly && !milestonesJoined ? sql`AND br.value->>'structural' = 'true'` : sql``}
    -- Distinct on sourceBlockId, then order by isCommitted (non-committed first, as they're overrides), and validFrom (newest first)
    ORDER BY
      br.value->>'sourceBlockId',
      CASE WHEN br.value->>'blockId' = (SELECT root_id FROM root_data) THEN 1 ELSE 0 END DESC,
      sl.priority,
      bl.priority,
      (br.value->>'validTo')::timestamptz DESC NULLS FIRST,
      (br.value->>'isCommitted')::boolean,
      (br.value->>'validFrom')::timestamptz DESC
  ),
  -- Filter out by validTo now:
  brs AS (
    SELECT key, value, version, spaceid, lastmodified
    FROM possible_brs br
    WHERE (
    -- we assume the validTo temporal check is already done if the priority is not 1
    br.branch_priority > 1
      -- else we do the validTo check
      OR br.value->>'validTo' IS NULL OR (br.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data)::timestamptz)
    -- we don't filter out placeholderSince items, since they need to, well, be placeholders.
    ${paginate ? sql`LIMIT ${paginate.limit} OFFSET ${paginate.cursor}` : sql``}
  ),
`

  if (mode !== 'brOnly') {
    result = result.append(sql`
blocks AS (
    SELECT DISTINCT ON (b.key)
        b.key, 
        b.value, 
        b.version, 
        b.spaceid,
        b.lastmodified
    FROM entry b
    JOIN brs br ON b.key = CONCAT('block/', br.value->>'blockId') AND b.spaceid IN (${sql.join(
      spaces,
      sql`,`
    )})
    JOIN space_lineage sl ON sl.space_id = b.spaceid
    ORDER BY b.key, sl.priority ASC
    `)

    result = result.append(sql`),`)
  }

  result = result.append(sql`
  all_entries_union AS (
    ${sql.raw(
      mode !== 'blockOnly'
        ? `
    SELECT *
    FROM brs`
        : ``
    )}
    ${sql.raw(mode === 'both' ? `UNION ALL` : ``)}
    ${sql.raw(
      mode !== 'brOnly'
        ? `
    SELECT *
    FROM blocks`
        : ``
    )}
    )
SELECT ae.key, ae.value, ae.version, ae.spaceid, ae.lastmodified
FROM all_entries_union ae
    `)

  if (cvrKey) {
    result = result.append(sql`
    LEFT JOIN cvr_normie c ON c.key = ${cvrKey} AND c.entrykey = ae.key
    WHERE (
      -- Entry not in cvr_normie
      (c.version IS NULL)
      OR 
      -- Entry IS in cvr_normie and has updates
      (c.version IS NOT NULL AND (ae.version > c.version OR c.syncsequence > ${cvrSyncSequence}))
    )
    `)
  }

  if (additionalFilters) {
    result = result.append(sql`\nWHERE `)
    result = result.append(additionalFilters('ae'))
  }

  result = result.append(sql`
ORDER BY ae.key`)

  return result
}

export const getNestedBlocksPaginated = async ({
  db,
  pageSize = 2500,
  ...rest
}: Omit<GetNestedBlocksSQL, 'paginate'> & {
  db: DB | DBNeonHttp
  pageSize?: number
}) => {
  const touched = new Set<string>()
  const blocks: Block[] = []
  const brs: BlockRelationship[] = []

  let timesToDB = 0
  let done = false
  let page = 0

  while (!done) {
    const sql = getNestedBlocksSQL({
      ...rest,
      paginate: {
        limit: pageSize,
        cursor: (page * pageSize).toString(),
      },
    })

    let result: PostgresExecuteResult = []
    const _result = await db.execute(sql)
    if (isDBNeonHttpResult(_result)) {
      result = _result.rows
    } else {
      result = _result
    }
    timesToDB++
    page++

    if (result.length < (rest.mode === 'both' ? pageSize * 2 : pageSize)) {
      done = true
    }

    for (const row of result) {
      const key = row.key as string
      if (!touched.has(key)) {
        touched.add(key)
        if (key.startsWith('br/')) {
          brs.push(row.value as BlockRelationship)
        } else {
          blocks.push(row.value as Block)
        }
      } else {
        throw new Error(
          'duplicate block while getting from the db. this is inefficient.'
        )
      }
    }
  }

  // Optional: Add a safety limit
  if (timesToDB > 1000) {
    throw new Error(
      'Fetched too many times. There might be an issue with the data or query.'
    )
  }

  return { blocks, brs }
}
